"use client";
import { useEffect, useRef, useCallback } from "react";
import { getLocationCookie } from '@/app/utilities/locationCookie';

// Classes to toggle for active/inactive nav items — must match navItemClasses()'s
// active/inactive sets in SecondaryNav.tsx exactly, since this only adds/removes
// these specific classes: any mismatch leaves stale classes stuck on an item
// once JS reassigns "active" to a different one on scroll.
const ACTIVE_CLS = ['!text-[#008C8B]', 'border-b-2', 'border-[#008C8B]', 'bg-white', '!font-[500]'];
const INACTIVE_CLS = ['md:text-gray-900', 'md:hover:text-black', 'text-gray-700'];

/**
 * Thin client wrapper — renders NO HTML itself.
 * Attaches all interactive behavior to the server-rendered nav via DOM refs:
 *   - IntersectionObserver for scroll-based active state
 *   - Click-to-smooth-scroll for [data-nav-type="scroll"] items
 *   - Price navigation for [data-nav-type="button"][data-nav-id="price"]
 *   - Location cookie → price URL updates
 *   - Fixed-position dropdown for [data-nav-type="dropdown"] <details> elements
 */
export default function SecondaryNavBehavior({
	children,
	initialActive,
	hasAdAboveNav = false,
}: {
	children: React.ReactNode;
	initialActive?: string;
	hasAdAboveNav?: boolean;
}) {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const ignoreObserverRef = useRef(false);
	const observerRef = useRef<IntersectionObserver | null>(null);

	const computeHeaderOffset = useCallback(() => {
		const headerHeight = (document.querySelector("header")?.getBoundingClientRect().height) ?? 65;
		const navHeight = (wrapperRef.current?.querySelector<HTMLElement>('[data-secondary-nav]')?.getBoundingClientRect().height) ?? 0;
		return headerHeight + navHeight;
	}, []);

	const setActive = useCallback((id: string) => {
		if (!wrapperRef.current) return;
		wrapperRef.current.querySelectorAll<HTMLElement>('[data-nav-id]').forEach(item => {
			if (item.getAttribute('data-nav-id') === id) {
				INACTIVE_CLS.forEach(c => item.classList.remove(c));
				ACTIVE_CLS.forEach(c => item.classList.add(c));
			} else {
				ACTIVE_CLS.forEach(c => item.classList.remove(c));
				INACTIVE_CLS.forEach(c => item.classList.add(c));
			}
		});
		
		// Scroll the active nav item into view, centered in the container
		requestAnimationFrame(() => {
			const container = wrapperRef.current?.querySelector<HTMLElement>('[data-scroll-container]');
			const activeEl = wrapperRef.current?.querySelector<HTMLElement>(`[data-nav-id="${CSS.escape(id)}"]`);
			if (container && activeEl) {
				const cRect = container.getBoundingClientRect();
				const iRect = activeEl.getBoundingClientRect();
				container.scrollTo({ left: container.scrollLeft + iRect.left - cRect.left - cRect.width / 2 + iRect.width / 2, behavior: 'smooth' });
			}
		});
	}, []);

	// Scroll to top on first mount
	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'auto' });
	}, []);

	// Dynamically adjust nav top position based on scroll and ad height
	useEffect(() => {
		if (!hasAdAboveNav) return;

		const navElement = wrapperRef.current?.querySelector<HTMLElement>('[data-secondary-nav]');
		if (!navElement) return;

		const updatePosition = () => {
			const isMd = window.matchMedia('(min-width: 768px)').matches;
			const headerHeight = document.querySelector("header")?.getBoundingClientRect().height ?? (isMd ? 88 : 73);
			
			// Find the height of the ad container preceding our wrapper
			let adHeight = 0;
			let prev = wrapperRef.current?.previousElementSibling as HTMLElement | null;
			while (prev) {
				const rect = prev.getBoundingClientRect();
				if (rect.height > 0) {
					adHeight = rect.height;
					break;
				}
				prev = prev.previousElementSibling as HTMLElement | null;
			}

			const minTop = isMd ? 88 : 73;
			const maxTop = headerHeight + adHeight;
			const currentTop = Math.max(minTop, maxTop - window.scrollY);
			navElement.style.top = `${currentTop}px`;
		};

		// Set up ResizeObserver to detect when the ad loading placeholder/ad changes height
		const resizeObserver = new ResizeObserver(() => {
			updatePosition();
		});

		let prev = wrapperRef.current?.previousElementSibling;
		while (prev) {
			resizeObserver.observe(prev);
			prev = prev.previousElementSibling;
		}

		// Raw scroll events can fire many times per animation frame — running the geometry
		// reads above on every single one forces far more synchronous layout work than the
		// browser can actually paint. Coalescing to one call per frame fixes that.
		let scrollTicking = false;
		const onScroll = () => {
			if (scrollTicking) return;
			scrollTicking = true;
			requestAnimationFrame(() => {
				updatePosition();
				scrollTicking = false;
			});
		};

		// Deferred a frame to avoid forcing a layout flush during mount.
		requestAnimationFrame(updatePosition);
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', updatePosition);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', updatePosition);
		};
	}, [hasAdAboveNav]);

	// Hide nav items whose target section doesn't exist in the page
	useEffect(() => {
		if (!wrapperRef.current) return;

		const updateVisibility = () => {
			wrapperRef.current
				?.querySelectorAll<HTMLElement>(
					'[data-nav-type="scroll"][data-nav-id]'
				)
				.forEach(item => {
					const id = item.getAttribute('data-nav-id');

					if (!id) return;

					item.style.display = document.getElementById(id)
						? ''
						: 'none';
				});
		};

		updateVisibility();

		// Observe DOM changes
		const observer = new MutationObserver(updateVisibility);

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => observer.disconnect();
	}, []);

	// Location cookie → update price URLs on mount and on location change event
	useEffect(() => {
		const updatePriceUrls = (slug: string) => {
			wrapperRef.current?.querySelectorAll<HTMLAnchorElement>('a[href*="/price-in-"]').forEach(link => {
				const updated = link.pathname.replace(/\/price-in-[^/]+$/, `/price-in-${slug}`);
				if (updated !== link.pathname) link.href = updated;
			});
		};
		const cookieLoc = getLocationCookie();
		if (cookieLoc?.slug) updatePriceUrls(cookieLoc.slug);

		const handler = (e: Event) => {
			const { location } = (e as CustomEvent).detail || {};
			if (location?.slug) updatePriceUrls(location.slug);
		};
		window.addEventListener('location-updated', handler);
		return () => window.removeEventListener('location-updated', handler);
	}, []);

	// Click handler: smooth-scroll items + price-button navigation
	useEffect(() => {
		const wrapper = wrapperRef.current;
		if (!wrapper) return;

		const handleClick = (e: MouseEvent) => {
			// If user clicked the Price anchor (<Link> renders <a>), the DOM href may be
			// updated from the location cookie, but Next's internal href remains the original.
			// Normal click uses Next navigation; ctrl/cmd click uses the DOM href.
			// Force normal clicks to follow the DOM href for /price-in-* links.
			const anchor = (e.target as HTMLElement | null)?.closest?.('a[href*="/price-in-"]') as HTMLAnchorElement | null;
			if (anchor) {
				// Let the browser handle new-tab / new-window behaviors.
				if (
					e.defaultPrevented ||
					e.button !== 0 ||
					e.metaKey ||
					e.ctrlKey ||
					e.shiftKey ||
					e.altKey
				) {
					return;
				}
				e.preventDefault();
				window.location.href = anchor.href;
				return;
			}

			const target = (e.target as HTMLElement).closest<HTMLElement>('[data-nav-id]');
			if (!target) return;
			const type = target.getAttribute('data-nav-type');
			const id = target.getAttribute('data-nav-id') || '';

			if (type === 'scroll') {
				const section = document.getElementById(id);
				if (!section) return;
				e.preventDefault();
				const offset = computeHeaderOffset();
				const offsetPosition = Math.max(0, section.getBoundingClientRect().top + window.scrollY - offset + 1);
				ignoreObserverRef.current = true;
				setActive(id);
				window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
				setTimeout(() => { ignoreObserverRef.current = false; }, 600);
			}

			if (type === 'button' && id === 'price') {
				e.preventDefault();
				try {
					const parts = window.location.pathname.split('/').filter(Boolean);
					if (parts.at(-1)?.startsWith('price-in-')) parts.pop();
					const citySlug = getLocationCookie()?.slug || 'new-delhi';
					const basePath = parts.length >= 2
						? `/${parts[0]}/${parts[1]}/price-in-${citySlug}`
						: `${window.location.pathname.replace(/\/$/, '')}/price-in-${citySlug}`;
					window.location.href = basePath;
				} catch { /* silently fail */ }
			}
		};

		wrapper.addEventListener('click', handleClick);
		return () => wrapper.removeEventListener('click', handleClick);
	}, [computeHeaderOffset, setActive]);

	// Dropdown: hide static list on mount, show with fixed positioning on <details> toggle
	useEffect(() => {
		const wrapper = wrapperRef.current;
		if (!wrapper) return;
		const cleanups: (() => void)[] = [];

		wrapper.querySelectorAll<HTMLDetailsElement>('[data-nav-type="dropdown"] details').forEach(details => {
			const summary = details.querySelector('summary');
			const list = details.querySelector<HTMLElement>('[data-dropdown-list]');
			if (!summary || !list) return;

			// Hide the static list; JS will reposition and show it
			list.style.display = 'none';

			const handleToggle = () => {
				if (details.open) {
					const rect = summary.getBoundingClientRect();
					list.style.position = 'fixed';
					list.style.top = `${rect.bottom + 4}px`;
					list.style.right = `${window.innerWidth - rect.right}px`;
					list.style.zIndex = '999';
					list.style.display = '';
				} else {
					list.style.display = 'none';
				}
			};

			const handleOutsideClick = (e: MouseEvent) => {
				if (!details.contains(e.target as Node)) details.open = false;
			};

			details.addEventListener('toggle', handleToggle);
			document.addEventListener('mousedown', handleOutsideClick);
			cleanups.push(() => {
				details.removeEventListener('toggle', handleToggle);
				document.removeEventListener('mousedown', handleOutsideClick);
			});
		});

		return () => cleanups.forEach(fn => fn());
	}, []);

	// IntersectionObserver: update active state based on scroll position
	useEffect(() => {
		let mounted = true;
		const wrapper = wrapperRef.current;
		if (!wrapper) return;

		// Collect all trackable section ids from DOM data attributes
		const navIds = Array.from(wrapper.querySelectorAll<HTMLElement>('[data-nav-id]'))
			.map(el => el.getAttribute('data-nav-id'))
			.filter((id): id is string => !!id);

		const updateActiveByDistance = (headerOffset: number) => {
			if (ignoreObserverRef.current || !mounted) return;
			if (window.scrollY < headerOffset) {
				const target = initialActive || navIds[0] || '';
				if (target) setActive(target);
				return;
			}

			const isAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10;
			let activeId = '';

			if (isAtBottom) {
				for (let i = navIds.length - 1; i >= 0; i--) {
					const id = navIds[i];
					const el = document.getElementById(id);
					if (el) {
						const rect = el.getBoundingClientRect();
						if (rect.top < window.innerHeight) {
							activeId = id;
							break;
						}
					}
				}
			}

			if (!activeId) {
				let maxTop = -Infinity;
				navIds.forEach(id => {
					const el = document.getElementById(id);
					if (!el) return;
					const rect = el.getBoundingClientRect();
					// The section is considered active if its top has scrolled past the header offset.
					// We add a small buffer (e.g. 24px) for subpixel and padding tolerances.
					if (rect.top <= headerOffset + 24) {
						if (rect.top > maxTop) {
							maxTop = rect.top;
							activeId = id;
						}
					}
				});
			}

			if (activeId) {
				setActive(activeId);
			} else {
				const target = initialActive || navIds[0] || '';
				if (target) setActive(target);
			}
		};

		const setupObserver = () => {
			const headerOffset = computeHeaderOffset();
			if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
			const obs = new IntersectionObserver(
				() => updateActiveByDistance(headerOffset),
				{ root: null, rootMargin: `-${headerOffset}px 0px -50% 0px`, threshold: [0, 0.25, 0.5, 0.75, 1] }
			);
			observerRef.current = obs;
			navIds.forEach(id => { const el = document.getElementById(id); if (el) obs.observe(el); });
			updateActiveByDistance(headerOffset);
		};

		// updateActiveByDistance reads getBoundingClientRect for every nav section — raw scroll
		// events can fire many times per animation frame, so without coalescing this forces
		// that same layout work far more often than the browser can actually paint.
		let scrollTicking = false;
		const handleScroll = () => {
			if (scrollTicking) return;
			scrollTicking = true;
			requestAnimationFrame(() => {
				updateActiveByDistance(computeHeaderOffset());
				scrollTicking = false;
			});
		};
		// Deferred a frame to avoid forcing a layout flush during mount.
		requestAnimationFrame(setupObserver);
		window.addEventListener('resize', setupObserver);
		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			mounted = false;
			if (observerRef.current) observerRef.current.disconnect();
			window.removeEventListener('resize', setupObserver);
			window.removeEventListener('scroll', handleScroll);
		};
	}, [computeHeaderOffset, initialActive, setActive]);

	return <div ref={wrapperRef}>{children}</div>;
}
